import { unwrap, type TeamOut } from "@sagot/api-client";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useBillingOverview } from "../features/workspace/shared/use-billing-overview";
import { wsKeys } from "../features/workspace/shared/query-keys";
import { queryKeys } from "../lib/query-keys";
import { useMembers } from "../roles/use-roles";
import { usePermissions } from "../session/permissions";
import { useApi } from "../session/session-provider";
import { useWorkspaceDetails, useWorkspaceId } from "../session/workspace-context";
import { planSeatLimit, seatState, type SeatState } from "./team-model";

const INVITES_PAGE_LIMIT = 200;

/** `GET /v1/invitations?status=pending` */
export function usePendingInvites() {
  const api = useApi();
  const wsId = useWorkspaceId();
  return useQuery({
    queryKey: [...queryKeys.invitations(wsId), "pending"],
    queryFn: async () => (await unwrap(api.GET("/v1/invitations", { params: { query: { status: "pending", limit: INVITES_PAGE_LIMIT } } }))).items,
  });
}

export interface SeatsInfo {
  state: SeatState | null;
  plan: string | undefined;
  /** Billing meter readable (billing.view) — else derived from members + plan. */
  fromBilling: boolean;
}

/** Seat usage: billing meter when the viewer can read billing, else active members vs the plan's seats. */
export function useSeats(): SeatsInfo {
  const permissions = usePermissions();
  const canReadBilling = permissions.can("billing.view");
  const overview = useBillingOverview(canReadBilling);
  const members = useMembers();
  const invites = usePendingInvites();
  const details = useWorkspaceDetails();
  const pending = invites.data?.length ?? 0;
  if (canReadBilling && overview.data) {
    const seats = overview.data.seats;
    return { state: seatState({ used: seats.used, limit: seats.limit, pending }), plan: overview.data.plan.key, fromBilling: true };
  }
  if (!members.data || !details.data) return { state: null, plan: details.data?.plan, fromBilling: false };
  const used = members.data.filter((m) => m.status === "active").length;
  return { state: seatState({ used, limit: planSeatLimit(details.data.plan), pending }), plan: details.data.plan, fromBilling: false };
}

/** `GET /v1/settings/security` allowed email domains (only readable with settings.view). */
export function useAllowedDomains(): readonly string[] {
  const api = useApi();
  const wsId = useWorkspaceId();
  const permissions = usePermissions();
  const query = useQuery({
    queryKey: wsKeys.security(wsId),
    queryFn: () => unwrap(api.GET("/v1/settings/security")),
    enabled: permissions.can("settings.view"),
    staleTime: 60_000,
  });
  return query.data?.allowed_email_domains ?? [];
}

export function useInviteActions() {
  const api = useApi();
  const wsId = useWorkspaceId();
  const client = useQueryClient();
  const refresh = () => client.invalidateQueries({ queryKey: queryKeys.invitations(wsId) });
  const resend = useMutation({
    mutationFn: (id: string) => unwrap(api.POST("/v1/invitations/{invitation_id}/resend", { params: { path: { invitation_id: id } } })),
    onSuccess: refresh,
  });
  const revoke = useMutation({
    mutationFn: (id: string) => unwrap(api.DELETE("/v1/invitations/{invitation_id}", { params: { path: { invitation_id: id } } })),
    onSuccess: refresh,
  });
  return { resend, revoke };
}

export interface TeamDraft {
  name: string;
  description: string | null;
  routing_topics: string[];
  round_robin: boolean;
  /** Optional shared mailbox for the team's handoffs and reminders. */
  notify_email: string | null;
  /** Membership id of the team lead (must be one of `membership_ids`). */
  lead_membership_id: string | null;
  membership_ids: string[];
}

/**
 * Create (one call: the team and its members are saved together, so a failure leaves nothing to
 * retry around) or update a team. An update sets the members first (a lead who left is cleared)
 * and then the fields, so the lead is checked against the final member list.
 */
export function useSaveTeam() {
  const api = useApi();
  const wsId = useWorkspaceId();
  const client = useQueryClient();
  return useMutation({
    mutationFn: async ({ team, draft }: { team: TeamOut | null; draft: TeamDraft }) => {
      const { membership_ids: membershipIds, ...fields } = draft;
      if (!team) return unwrap(api.POST("/v1/teams", { body: { ...fields, membership_ids: membershipIds } }));
      const path = { params: { path: { team_id: team.id } } };
      await unwrap(api.PUT("/v1/teams/{team_id}/members", { ...path, body: { membership_ids: membershipIds } }));
      return unwrap(api.PATCH("/v1/teams/{team_id}", { ...path, body: fields }));
    },
    onSuccess: () => Promise.all([client.invalidateQueries({ queryKey: queryKeys.teams(wsId) }), client.invalidateQueries({ queryKey: queryKeys.members(wsId) })]),
  });
}

export function useDeleteTeam() {
  const api = useApi();
  const wsId = useWorkspaceId();
  const client = useQueryClient();
  return useMutation({
    mutationFn: (teamId: string) => unwrap(api.DELETE("/v1/teams/{team_id}", { params: { path: { team_id: teamId } } })),
    onSuccess: () => Promise.all([client.invalidateQueries({ queryKey: queryKeys.teams(wsId) }), client.invalidateQueries({ queryKey: queryKeys.members(wsId) })]),
  });
}

/** Extra seats add-on (`POST /v1/billing/addons {addon_key: "extra_seat"}`), billing.manage. */
export function useAddSeats() {
  const api = useApi();
  const wsId = useWorkspaceId();
  const client = useQueryClient();
  return useMutation({
    mutationFn: (quantity: number) => unwrap(api.POST("/v1/billing/addons", { body: { addon_key: "extra_seat", quantity, save_method: true } })),
    onSuccess: () => client.invalidateQueries({ queryKey: wsKeys.billing(wsId) }),
  });
}
